import React, { useState, useEffect, useRef } from 'react';
import { Container, Typography, Box, Grid, Paper, Button, IconButton, Fade, Slide, Zoom, Grow } from '@mui/material';
import { 
  CodeRounded, 
  CloudRounded,
  PhoneAndroidRounded,
  CampaignRounded,
  ArrowDownward, 
  Speed, 
  People, 
  Timeline,
  Star,
  FormatQuote,
  NavigateBefore,
  NavigateNext
} from '@mui/icons-material';

function Home() {
  const partnersRef = useRef(null);
  
  const scrollToAboutUs = () => {
    const element = document.getElementById('about-us-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  const partners = [
    { name: 'RAS', logo: '/raslogo.png' },
    { name: 'NLT', logo: '/nltlogo.png' },
    { name: 'GASS', logo: '/gasslogo.png' },
    { name: 'PBT Music', logo: '/pbtmusiclogo.png' },
    { name: 'Dekouvri', logo: '/dekouvrilogo.png' }
  ];

  // CSS animation handles the carousel rotation automatically

  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [isVisible, setIsVisible] = useState({});
  const [animateHero, setAnimateHero] = useState(false);
  const [animateServices, setAnimateServices] = useState(false);

  const testimonials = [
    {
      name: 'John Smith',
      role: 'CEO, Tech Innovations',
      content: 'Pierre Brothers Tech transformed our business with their innovative solutions. Their expertise and dedication are unmatched.',
      rating: 5
    },
    {
      name: 'Sarah Johnson',
      role: 'CTO, Digital Dynamics',
      content: 'Outstanding service and technical expertise. They delivered our project ahead of schedule with exceptional quality.',
      rating: 5
    },
    {
      name: 'Michael Chen',
      role: 'Founder, StartUp Hub',
      content: 'Their cybersecurity solutions gave us peace of mind. Professional team with cutting-edge knowledge.',
      rating: 5
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          setIsVisible(prev => ({
            ...prev,
            [entry.target.id]: entry.isIntersecting
          }));
        });
      },
      { threshold: 0.2 }
    );

    document.querySelectorAll('[data-animate]').forEach(el => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  // Trigger hero animations on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimateHero(true);
    }, 300);
    
    const servicesTimer = setTimeout(() => {
      setAnimateServices(true);
    }, 800);

    return () => {
      clearTimeout(timer);
      clearTimeout(servicesTimer);
    };
  }, []);

  const handleTestimonialChange = (direction) => {
    setActiveTestimonial(prev => {
      if (direction === 'next') {
        return (prev + 1) % testimonials.length;
      } else {
        return prev === 0 ? testimonials.length - 1 : prev - 1;
      }
    });
  };

  const renderStars = (rating) => {
    return [...Array(rating)].map((_, i) => (
      <Star key={i} sx={{ color: '#FFD700', fontSize: 20 }} />
    ));
  };
  const services = [
    {
      icon: <CodeRounded sx={{ fontSize: 40 }} />,
      title: 'Custom Software Development',
      description: 'Tailored solutions designed around your business goals.'
    },
    {
      icon: <CloudRounded sx={{ fontSize: 40 }} />,
      title: 'Cloud & Networking Solutions',
      description: 'Secure, scalable, and optimized cloud infrastructures.'
    },
    {
      icon: <PhoneAndroidRounded sx={{ fontSize: 40 }} />,
      title: 'Mobile Application Development',
      description: 'Modern, user-centric apps for iOS and Android.'
    },
    {
      icon: <CampaignRounded sx={{ fontSize: 40 }} />,
      title: 'Digital Marketing',
      description: 'Strategies that help you reach and engage your audience effectively.'
    }
  ];

  return (
    <Box>
      {/* Jumbotron Section */}
      <Box sx={{
        minHeight: { xs: '70vh', md: '80vh' },
        display: 'flex',
        alignItems: 'center',
        color: 'white',
        position: 'relative',
        textAlign: 'center'
      }}>
        <Container>
          <Fade in={animateHero} timeout={1000}>
            <Typography variant="h1" component="h1" 
              sx={{ 
                fontSize: { xs: '2.5rem', md: '4rem' },
                fontWeight: 'bold',
                mb: 3
              }}>
              Welcome to Pierre Brothers Tech
            </Typography>
          </Fade>
          <Slide direction="up" in={animateHero} timeout={1200}>
            <Typography variant="h4" 
              sx={{ 
                mb: 5,
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: { xs: '1.5rem', md: '2rem' }
              }}>
              Delivering innovative technology solutions for tomorrow's challenges
            </Typography>
          </Slide>
          <Zoom in={animateHero} timeout={1500}>
            <Button
              variant="outlined"
              color="inherit"
              size="large"
              endIcon={<ArrowDownward />}
              onClick={scrollToAboutUs}
              sx={{ 
                borderWidth: 2,
                '&:hover': { 
                  borderWidth: 2,
                  transform: 'scale(1.05)',
                  transition: 'transform 0.2s ease-in-out'
                }
              }}
            >
              Discover More
            </Button>
          </Zoom>
        </Container>
      </Box>

      {/* About Us Section */}
      <Box id="about-us-section" sx={{ 
        py: { xs: 6, md: 8 },
        bgcolor: 'rgba(0, 0, 0, 0.7)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background Image */}
        <Box sx={{
          position: 'absolute',
          top: '40%',
          left: { xs: '0%', md: '5%' },
          transform: 'translateY(-50%)',
          width: { xs: '350px', md: '500px' },
          height: { xs: '250px', md: '350px' },
          backgroundImage: 'url(/aboutsecpic.png)',
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          borderRadius: '12px',
          opacity: 0.6,
          zIndex: 0,
          animation: 'float 6s ease-in-out infinite'
        }} />

        {/* Bottom Right Image */}
        <Box sx={{
          position: 'absolute',
          bottom: { xs: '40px', md: '60px' },
          right: { xs: '40px', md: '80px' },
          width: { xs: '320px', md: '450px' },
          height: { xs: '240px', md: '320px' },
          backgroundImage: 'url(/abouttab.png)',
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          borderRadius: '8px',
          opacity: 0.7,
          zIndex: 0,
          animation: 'float 8s ease-in-out infinite reverse'
        }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
            <Typography 
              variant="overline" 
              sx={{ 
                color: 'primary.main',
                letterSpacing: 2,
                fontWeight: 'bold',
                mb: { xs: 2, md: 2 },
                display: 'block'
              }}
            >
              ABOUT US
            </Typography>
            <Typography 
              variant="h2" 
              sx={{ 
                color: 'white',
                fontWeight: 'bold',
                mb: { xs: 3, md: 4 },
                fontSize: { xs: '2rem', md: '3rem' }
              }}
            >
              Who We Are
            </Typography>
          </Box>

          {/* Content Layout */}
          <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.8)',
                lineHeight: 1.7,
                fontSize: { xs: '1rem', md: '1.1rem' },
                mb: 4,
                textAlign: 'center'
              }}
            >
              Pierre Brothers Tech is a forward-thinking technology company dedicated to delivering 
              innovative digital solutions that drive transformation and growth. Founded by the Pierre 
              brothers, our journey began after years of working in the tech industry and recognizing 
              a common problem — poorly built software and the lack of customized solutions that truly 
              meet business needs.
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.8)',
                lineHeight: 1.7,
                fontSize: { xs: '1rem', md: '1.1rem' },
                mb: 4,
                textAlign: 'center'
              }}
            >
              Determined to make a difference, we launched Pierre Brothers Tech to bridge that gap and 
              bring quality, reliability, and creativity to every project we touch.
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.8)',
                lineHeight: 1.7,
                fontSize: { xs: '1rem', md: '1.1rem' },
                mb: 4,
                textAlign: 'center'
              }}
            >
              Our combined expertise spans across multiple areas of technology — Wilgens Pierre holds 
              a Master's degree in Computer Science, while Wilson Pierre is a certified AWS Cloud 
              Solutions Architect and Full-Stack Developer with extensive experience in cloud 
              networking and application development.
            </Typography>
          </Box>

          {/* Services and Mission Section */}
          <Box sx={{ mt: 6, textAlign: 'center' }}>
            <Typography 
              variant="h6" 
              sx={{ 
                color: 'primary.main',
                fontWeight: 'bold',
                mb: 3
              }}
            >
              We specialize in:
            </Typography>
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 2,
              maxWidth: '800px',
              mx: 'auto',
              mb: 4
            }}>
              <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)', textAlign: 'left' }}>
                • <strong>Custom Software Development</strong> – tailored solutions designed around your business goals.
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)', textAlign: 'left' }}>
                • <strong>Cloud & Networking Solutions</strong> – secure, scalable, and optimized cloud infrastructures.
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)', textAlign: 'left' }}>
                • <strong>Mobile Application Development</strong> – modern, user-centric apps for iOS and Android.
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)', textAlign: 'left' }}>
                • <strong>Digital Marketing</strong> – strategies that help you reach and engage your audience effectively.
              </Typography>
            </Box>
            <Typography 
              variant="h6" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.9)',
                maxWidth: '800px',
                mx: 'auto',
                lineHeight: 1.6,
                fontSize: { xs: '1.1rem', md: '1.2rem' },
                fontStyle: 'italic'
              }}
            >
              At Pierre Brothers Tech, our mission is simple — to empower businesses with technology 
              that works as hard as they do.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Why Choose Us Section */}
      <Box id="why-choose-us-section" sx={{ 
        py: { xs: 5, md: 6 },
        bgcolor: 'rgba(255, 255, 255, 0.02)',
        position: 'relative',
        overflow: 'hidden',
        minHeight: { xs: 'auto', md: '80vh' }
      }}>
        <Container maxWidth="lg" sx={{ display: 'flex', flexDirection: 'column', height: '100%', py: { xs: 2, md: 3 } }}>
          <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 4 } }}>
            <Typography 
              variant="overline" 
              sx={{ 
                color: 'primary.main',
                letterSpacing: 2,
                fontWeight: 'bold',
                mb: { xs: 2, md: 2 },
                display: 'block'
              }}
            >
              WHY CHOOSE US
            </Typography>
            <Typography 
              variant="h2" 
              sx={{ 
                color: 'white',
                fontWeight: 'bold',
                mb: { xs: 5, md: 4 },
                fontSize: { xs: '2rem', md: '3rem' }
              }}
            >
              The Pierre Brothers Advantage
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                color: 'rgba(255,255,255,0.7)',
                maxWidth: '800px',
                mx: 'auto',
                mb: 6
              }}
            >
              We combine technical expertise with business acumen to deliver exceptional results
            </Typography>
          </Box>

          <Grid 
            container 
            spacing={4} 
            sx={{ 
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'center',
              alignItems: 'stretch'
            }}
          >
            {[
              { 
                icon: <Speed sx={{ fontSize: 56 }} />, 
                title: 'Fast Delivery', 
                description: 'Quick turnaround on all projects while maintaining the highest quality standards',
                color: '#00E676'
              },
              { 
                icon: <People sx={{ fontSize: 56 }} />, 
                title: 'Expert Team', 
                description: 'Skilled professionals with years of industry experience and continuous learning mindset',
                color: '#0288D1'
              },
              { 
                icon: <Timeline sx={{ fontSize: 56 }} />, 
                title: 'Proven Track Record', 
                description: 'Successfully delivered numerous projects across various industries with measurable impact',
                color: '#7C4DFF'
              }
            ].map((item, index) => (
              <Grid 
                item 
                xs={12} 
                md={4} 
                key={index}
                sx={{ 
                  display: 'flex',
                  flex: { xs: '0 0 auto', md: '1 1 0px' },
                  maxWidth: { xs: '100%', md: '33.333%' }
                }}
              >
                <Paper 
                  elevation={12} 
                  sx={{
                    p: { xs: 4, md: 4 },
                    display: 'flex',
                    flexDirection: 'column',
                    gap: { xs: 4, md: 3 },
                    flex: 1,
                    height: { xs: 'auto', md: '100%' },
                    textAlign: 'left',
                    background: 'rgba(255, 255, 255, 0.03)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: 4,
                    color: 'white',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      background: 'rgba(255, 255, 255, 0.05)',
                      boxShadow: `0 12px 20px -10px ${item.color}40`
                    }
                  }}
                >
                  <Box 
                    sx={{ 
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      color: item.color
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography 
                    variant="h5" 
                    component="h3" 
                    sx={{
                      fontWeight: 'bold',
                      color: 'white'
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    {item.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Services Section */}
      <Box sx={{ 
        py: { xs: 5, md: 6 },
        position: 'relative',
        minHeight: { xs: 'auto', md: '80vh' }
      }}>
        <Container maxWidth="lg" sx={{ display: 'flex', flexDirection: 'column', height: '100%', py: { xs: 2, md: 3 } }}>
          <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 4 } }}>
            <Typography 
              variant="overline" 
              sx={{ 
                color: 'secondary.main',
                letterSpacing: 2,
                fontWeight: 'bold',
                mb: { xs: 2, md: 2 },
                display: 'block'
              }}
            >
              OUR EXPERTISE
            </Typography>
            <Typography 
              variant="h2" 
              sx={{ 
                color: 'white',
                fontWeight: 'bold',
                mb: { xs: 3, md: 3 },
                fontSize: { xs: '1.75rem', md: '2.5rem' }
              }}
            >
              Comprehensive Tech Solutions
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                color: 'rgba(255,255,255,0.7)',
                maxWidth: '800px',
                mx: 'auto',
                mb: { xs: 4, md: 5 },
                fontSize: { xs: '1rem', md: '1.25rem' }
              }}
            >
              End-to-end services tailored to your business needs
            </Typography>
          </Box>

          <Grid container spacing={{ xs: 4, md: 4 }} sx={{ flex: 1 }}>
            {services.map((service, index) => (
              <Grid 
                item 
                xs={12} 
                md={3} 
                key={index}
                sx={{ 
                  display: 'flex',
                  flex: { xs: '0 0 auto', md: '1 1 0px' },
                  maxWidth: { xs: '100%', md: '25%' }
                }}
              >
                <Grow 
                  in={animateServices} 
                  timeout={1000 + (index * 200)}
                  style={{ transformOrigin: '0 0 0' }}
                >
                  <Paper 
                  elevation={12} 
                  sx={{ 
                    p: { xs: 3, md: 4 },
                    display: 'flex',
                    flexDirection: 'column',
                    gap: { xs: 3, md: 3 },
                    flex: 1,
                    height: { xs: 'auto', md: '100%' },
                    textAlign: 'left',
                    background: 'rgba(255, 255, 255, 0.03)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: 4,
                    color: 'white',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translateY(-12px) scale(1.02)',
                      background: 'rgba(255, 255, 255, 0.08)',
                      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      '& .service-icon': {
                        transform: 'scale(1.2) rotate(5deg)',
                        color: '#00E676'
                      }
                    }
                  }}
                >
                  <Box 
                    className="service-icon"
                    sx={{ 
                      color: 'primary.main',
                      transform: 'scale(1)',
                      transition: 'transform 0.3s ease-in-out'
                    }}
                  >
                    {React.cloneElement(service.icon, { sx: { fontSize: 48 } })}
                  </Box>
                  <Typography 
                    variant="h5" 
                    component="h3" 
                    sx={{
                      fontWeight: 'bold',
                      color: 'white'
                    }}
                  >
                    {service.title}
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    {service.description}
                  </Typography>
                </Paper>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Testimonials Section */}
      <Box id="testimonials-section" data-animate="true" sx={{ py: 12, bgcolor: 'rgba(0, 0, 0, 0.4)', position: 'relative' }}>
        <Fade in={isVisible['testimonials-section']} timeout={1000}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: { xs: 10, md: 6 } }}>
              <Typography variant="overline" sx={{ color: 'primary.main', letterSpacing: 2, fontWeight: 'bold', mb: { xs: 3, md: 2 }, display: 'block' }}>
                TESTIMONIALS
              </Typography>
              <Typography variant="h2" sx={{ color: 'white', fontWeight: 'bold', mb: { xs: 5, md: 4 }, fontSize: { xs: '2rem', md: '3rem' } }}>
                What Our Clients Say
              </Typography>
            </Box>

            <Box sx={{ position: 'relative', maxWidth: 800, mx: 'auto', px: { xs: 4, md: 8 } }}>
              <IconButton onClick={() => handleTestimonialChange('prev')} sx={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
                <NavigateBefore />
              </IconButton>

              <Fade in={true} timeout={500}>
                <Paper elevation={12} sx={{ p: 6, background: 'rgba(255, 255, 255, 0.03)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: 4, color: 'white', textAlign: 'center', position: 'relative' }}>
                  <FormatQuote sx={{ fontSize: 60, color: 'primary.main', opacity: 0.4, position: 'absolute', top: 20, left: 20 }} />
                  <Typography variant="h5" sx={{ mb: 3, fontStyle: 'italic', color: 'rgba(255,255,255,0.9)' }}>
                    "{testimonials[activeTestimonial].content}"
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    {renderStars(testimonials[activeTestimonial].rating)}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {testimonials[activeTestimonial].name}
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    {testimonials[activeTestimonial].role}
                  </Typography>
                </Paper>
              </Fade>

              <IconButton onClick={() => handleTestimonialChange('next')} sx={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
                <NavigateNext />
              </IconButton>
            </Box>
          </Container>
        </Fade>
      </Box>

      {/* Partners Section */}
      <Box 
        id="partners-section"
        data-animate="true"
        sx={{ 
          py: 12,
          bgcolor: 'rgba(255, 255, 255, 0.02)',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Fade in={isVisible['partners-section']} timeout={1000}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: { xs: 10, md: 6 } }}>
              <Typography 
                variant="overline" 
                sx={{ 
                  color: 'secondary.main',
                  letterSpacing: 2,
                  fontWeight: 'bold',
                  mb: 2,
                  display: 'block'
                }}
              >
                OUR CLIENTS
              </Typography>
              <Typography 
                variant="h2" 
                sx={{ 
                  color: 'white',
                  fontWeight: 'bold',
                  mb: { xs: 3, md: 3 },
                  fontSize: { xs: '1.75rem', md: '2.5rem' }
                }}
              >
                Who We Serve
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: 'rgba(255,255,255,0.7)',
                  maxWidth: '800px',
                  mx: 'auto',
                  mb: 6
                }}
              >
                We've helped these companies achieve their technology goals
              </Typography>
            </Box>

            <Box 
              ref={partnersRef}
              sx={{ 
                overflow: 'hidden',
                position: 'relative',
                '&::before, &::after': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  width: '100px',
                  zIndex: 2,
                  pointerEvents: 'none'
                },
                '&::before': {
                  left: 0,
                  background: 'linear-gradient(to right, rgba(0,0,0,0.3), transparent)'
                },
                '&::after': {
                  right: 0,
                  background: 'linear-gradient(to left, rgba(0,0,0,0.3), transparent)'
                }
              }}
            >
              <Box sx={{ 
                display: 'flex',
                gap: 6,
                py: 4,
                animation: 'scroll 60s linear infinite',
                animationPlayState: 'running',
                '& > div': {
                  flex: '0 0 220px',
                  height: '140px',
                  background: 'rgba(255, 255, 255, 0.03)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 0,
                  overflow: 'hidden',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    background: 'rgba(255, 255, 255, 0.05)'
                  }
                }
              }}>
                {[...partners, ...partners].map((partner, index) => (
                  <Box key={index} sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center'
                  }}>
                    <img 
                      src={partner.logo} 
                      alt={`${partner.name} logo`}
                      style={{ 
                        height: '100%',
                        width: '100%',
                        objectFit: 'contain',
                        opacity: 0.9,
                        transition: 'all 0.3s ease-in-out',
                        borderRadius: '8px'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.opacity = '1';
                        e.target.style.transform = 'scale(1.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.opacity = '0.9';
                        e.target.style.transform = 'scale(1)';
                      }}
                      onError={(e) => {
                        console.log('Image failed to load:', partner.logo);
                        e.target.style.display = 'none';
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          </Container>
        </Fade>
      </Box>
    </Box>
  );
}

export default Home;
